import { BPDFormElement } from '@atoms/form-elements';
import { extendedArea } from '@bpd-library/utilities';
import { areaSubscriptionStore } from '@features/area-subscription/store';
import {
    getSubscriptionState,
    subscribeToAreaProject,
    subscribeToBatch,
} from '@store/subscription/store';
import { SubscriptionStoreAddress, SubscriptionStoreUser } from '@store/subscription/store.types';
import {
    AreaProjectSubscriptionProps,
    BatchSubscriptionProps,
    ObjectSubscription,
    ProjectSubscription,
    PropertyTypeSubscription,
} from '@utilities/api-calls';

type AreaSubscriptionFormData = {
    userInfo: SubscriptionStoreUser;
};

type SubscriptionFormData = AreaSubscriptionFormData & {
    address: SubscriptionStoreAddress;
};

const extractFormData = (
    form: BPDFormElement | undefined,
    options?: { areaOnly: boolean },
): AreaSubscriptionFormData | SubscriptionFormData => {
    let formData: SubscriptionFormData | AreaSubscriptionFormData;
    const { formRequired } = areaSubscriptionStore.getState();

    if (formRequired) {
        if (!form) {
            throw new Error('[Subscription] Form required but no data is available');
        }
        const {
            email,
            gender,
            country,
            city,
            street,
            zipcode,
            ['first-name']: firstName,
            ['last-name-prefix']: lastNamePrefix,
            ['last-name']: lastName,
            ['house-number']: houseNumber,
            ['house-number-addition']: houseNumberAddition,
            ['phone-number']: phoneNumber,
            website,
        } = form.serializeForm();

        formData = {
            userInfo: { firstName, lastName, lastNamePrefix, email, gender, website },
        } as AreaSubscriptionFormData;

        if (!options?.areaOnly) {
            formData = {
                ...formData,
                address: {
                    zipcode,
                    houseNumber,
                    houseNumberAddition,
                    street,
                    city,
                    phoneNumber,
                    country,
                },
            } as SubscriptionFormData;
        }
    } else {
        const { user, address } = getSubscriptionState();
        if (!user) {
            throw new Error('[Subscription] Form apparently not required but no user is found');
        }

        formData = { userInfo: user } as AreaSubscriptionFormData;
        if (address) formData = { ...formData, address } as SubscriptionFormData;
    }

    return formData;
};

const submitAreaForm = async (
    formData: AreaSubscriptionFormData,
    fcToken: string,
): Promise<AreaProjectSubscriptionProps> => {
    const { districtId, areaId } = window.__ENVIRONMENT_DETAILS__;

    const areaOnlyData: AreaProjectSubscriptionProps = {
        subscriptionInfo: districtId
            ? { areaId, districtId, type: 'district' }
            : { areaId, type: 'area' },
        userInfo: formData.userInfo,
        fcToken,
    };

    await subscribeToAreaProject(areaOnlyData);
    return areaOnlyData;
};

const submitAreaAndProjectPhasesForm = async (formData: SubscriptionFormData, fcToken: string) => {
    const { selectedProjectPhases, areaSelected } = areaSubscriptionStore.getState();
    const { areaId, districtId } = window.__ENVIRONMENT_DETAILS__;
    const { projectPhases } = await extendedArea.getData(areaId);

    const subscriptions: ObjectSubscription[] = projectPhases
        .filter(({ id }) => selectedProjectPhases.includes(id))
        .map<ProjectSubscription>((project) => ({
            type: 'project',
            areaId,
            districtId: project.districtId ?? districtId ?? '',
            projectPhaseId: project.id,
        }));

    if (areaSelected) {
        subscriptions.push(
            districtId ? { areaId, districtId, type: 'district' } : { areaId, type: 'area' },
        );
    }

    const batchForm: BatchSubscriptionProps = { ...formData, subscriptions, fcToken };
    await subscribeToBatch(batchForm);

    return batchForm;
};

const submitPropertyTypesForm = async (formData: SubscriptionFormData, fcToken: string) => {
    const { selectedPropertyTypes } = areaSubscriptionStore.getState();
    const { areaId, districtId } = window.__ENVIRONMENT_DETAILS__;
    const { propertyTypes } = await extendedArea.getData(areaId);

    const subscriptions = propertyTypes
        .filter(({ id }) => selectedPropertyTypes.includes(id))
        .map<PropertyTypeSubscription>((property) => ({
            type: 'propertyType',
            areaId,
            districtId: property?.districtId ?? districtId ?? '',
            projectPhaseId: property.projectPhaseId ?? '',
            propertyTypeId: property.id,
        }));

    const batchForm: BatchSubscriptionProps = { ...formData, subscriptions, fcToken };
    await subscribeToBatch(batchForm);

    return batchForm;
};

const submitSelection = async ({
    step,
    formData,
    fcToken,
}: {
    step: number;
    formData: AreaSubscriptionFormData | SubscriptionFormData;
    fcToken: string;
}) => {
    const { areaOnly } = areaSubscriptionStore.getState();

    switch (step) {
        case 0:
        case 1: {
            if (areaOnly) {
                await submitAreaForm(formData, fcToken);
            } else {
                await submitAreaAndProjectPhasesForm(formData as SubscriptionFormData, fcToken);
            }
            break;
        }
        case 2: {
            await submitPropertyTypesForm(formData as SubscriptionFormData, fcToken);
            break;
        }
    }
};

export { extractFormData, submitSelection };
